import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/shared/lib/axios';
import { clinicAuthPath } from '@/shared/lib/apiPath';
import { LoginCredentials, LoginResponse, User, Clinic } from '@/types/auth';
import {
  clearSsoSession,
  hasPortalCookieSession,
  isSsoSite,
  markPortalAuthChannel,
  type SsoEntryType,
} from '@/features/sso/ssoSession';

export type UnifiedTenantPrincipalType = 'admin' | 'clinic_user' | 'massager';

interface UnifiedTenantSession {
  user: User & { principal_type: UnifiedTenantPrincipalType };
  clinic: Clinic;
}

// ==================== API Service Functions ====================

/**
 * Login with clinic credentials
 */
const login = async (credentials: LoginCredentials): Promise<LoginResponse> => {
  const site = credentials.site_type;
  if (!site) {
    throw new Error('site_type is required for login');
  }
  const adminToken = credentials.fromAdminClinicUser
    ? localStorage.getItem('site_admin_token') || localStorage.getItem('admin_token') || undefined
    : undefined;
  const loginPath = credentials.authEntryType === 'unified'
    ? `/portal/${site}/auth/login`
    : clinicAuthPath(site, '/auth/login');
  const response = await apiClient.post<LoginResponse>(loginPath, {
    clinic_number: credentials.clinic_number,
    login_id: credentials.login_id,
    password: credentials.password,
    from_admin: credentials.fromAdminClinicUser,
  }, adminToken ? {
    headers: {
      Authorization: `Bearer ${adminToken}`,
    },
  } : undefined);
  return response.data;
};

/**
 * Logout current user
 */
type LogoutResponse = {
  success: boolean;
  data?: { logout_url?: unknown } | null;
};

const logout = async (site: string | undefined): Promise<LogoutResponse | undefined> => {
  try {
    if (!site) {
      throw new Error('site is required for logout');
    }
    const response = await apiClient.post<LogoutResponse>(clinicAuthPath(site, '/auth/logout'));
    return response.data;
  } catch {
    // Ignore errors on logout
    return undefined;
  }
};

/**
 * Get current authenticated user
 */
const getUser = async (site: string): Promise<{ user: User; clinic: Clinic | null }> => {
  const response = await apiClient.get(clinicAuthPath(site, '/auth/user'));
  return response.data.data;
};

/**
 * Refresh authentication token
 */
const refreshToken = async (site: string): Promise<{ success: boolean; data?: { token: string } }> => {
  const response = await apiClient.post(clinicAuthPath(site, '/auth/refresh'));
  return response.data;
};

const exactBearerConfig = (bearerToken: string | null | undefined) =>
  bearerToken?.trim()
    ? { headers: { Authorization: `Bearer ${bearerToken.trim()}` } }
    : undefined;

const getUnifiedTenantSession = async (
  site: string,
  _principalType: UnifiedTenantPrincipalType,
  bearerToken?: string | null
): Promise<UnifiedTenantSession> => {
  const response = await apiClient.get(
    `/portal/${site}/me/session`,
    exactBearerConfig(bearerToken)
  );
  return response.data.data as UnifiedTenantSession;
};

export const unifiedTenantSessionQueryKey = (
  site: string | undefined,
  principalType: UnifiedTenantPrincipalType,
  principalId: string | number | null | undefined,
  authGeneration: number,
) => [
  'auth',
  'unified-tenant-user',
  site,
  principalType,
  principalId ?? null,
  authGeneration,
] as const;

const unifiedPortalAuthPath = (
  site: string,
  entryType: SsoEntryType,
  suffix: '/logout' | '/refresh'
): string =>
  entryType === 'unified'
    ? `/portal/${site}/auth${suffix}`
    : entryType === 'admin'
      ? `/admin/${site}${suffix}`
      : clinicAuthPath(site, `/auth${suffix}`);

const unifiedLogout = async (
  site: string,
  entryType: SsoEntryType,
  bearerToken?: string | null
): Promise<LogoutResponse | undefined> => {
  try {
    const response = await apiClient.post<LogoutResponse>(
      unifiedPortalAuthPath(site, entryType, '/logout'),
      undefined,
      exactBearerConfig(bearerToken)
    );
    return response.data;
  } catch {
    return undefined;
  }
};

const unifiedRefresh = async (
  site: string,
  entryType: SsoEntryType,
  bearerToken?: string | null
): Promise<{ success: boolean; data?: { token: string } }> => {
  const response = await apiClient.post(
    unifiedPortalAuthPath(site, entryType, '/refresh'),
    undefined,
    exactBearerConfig(bearerToken)
  );
  return response.data;
};

/**
 * Request password reset email
 */
const forgotPassword = async (
  site: string,
  clinicNumber: string,
  loginId: string
): Promise<{ success: boolean; message: string }> => {
  const response = await apiClient.post(clinicAuthPath(site, '/auth/forgot-password'), {
    clinic_number: clinicNumber,
    login_id: loginId,
  });
  return response.data;
};

/**
 * Validate password reset token
 */
const validateResetToken = async (
  site: string,
  token: string,
  clinicNumber: string,
  loginId: string
): Promise<{ success: boolean; data?: { valid: boolean } }> => {
  const response = await apiClient.post(clinicAuthPath(site, '/auth/validate-reset-token'), {
    token,
    clinic_number: clinicNumber,
    login_id: loginId,
  });
  return response.data;
};

/**
 * Reset password with token
 */
const resetPassword = async (
  site: string,
  token: string,
  clinicNumber: string,
  loginId: string,
  password: string,
  passwordConfirmation: string
): Promise<{ success: boolean; message: string }> => {
  const response = await apiClient.post(clinicAuthPath(site, '/auth/reset-password'), {
    token,
    clinic_number: clinicNumber,
    login_id: loginId,
    password,
    password_confirmation: passwordConfirmation,
  });
  return response.data;
};

/**
 * Store authentication data
 */
const storeAuth = (data: LoginResponse['data']): void => {
  localStorage.setItem('auth_token', data.token);
  localStorage.setItem('auth_user', JSON.stringify(data.user));
  localStorage.setItem('auth_clinic', JSON.stringify(data.clinic));
};

/**
 * Store token
 */
const storeToken = (token: string): void => {
  localStorage.setItem('auth_token', token);
};

/**
 * Get stored token
 */
const getStoredToken = (): string | null => {
  return localStorage.getItem('auth_token');
};

// ==================== Hooks ====================

/**
 * Hook for user login
 */
export const useMainLogin = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (credentials: LoginCredentials) => login(credentials),
    onSuccess: (data: LoginResponse, variables) => {
      if (data.success && data.data) {
        // Store the credential and principal metadata before publishing which
        // exact browser auth channel is now active.
        storeAuth(data.data);
        if (isSsoSite(variables.site_type)) {
          const entryType = variables.authEntryType === 'unified' ? 'unified' : 'clinic';
          clearSsoSession({ site: variables.site_type, entryType });
          markPortalAuthChannel(
            { site: variables.site_type, entryType },
            'bearer',
            localStorage
          );
        }
        // Invalidate user query to refetch after login
        queryClient.invalidateQueries({ queryKey: ['auth', 'user'] });
      }
    },
  });
};

/**
 * Hook for user logout
 */
export const useMainLogout = () => {
  return useMutation({
    mutationFn: ({ site }: { site: string }) => logout(site),
  });
};

/**
 * Hook to get current authenticated user
 */
export const useMainGetUser = (site?: string) => {
  const hasCookieSession =
    isSsoSite(site) &&
    hasPortalCookieSession(
      { site, entryType: 'clinic' },
      localStorage,
      sessionStorage
    );

  return useQuery({
    queryKey: ['auth', 'user', site],
    queryFn: () => {
      if (!site) {
        throw new Error('site is required to fetch user');
      }
      return getUser(site);
    },
    enabled: (!!getStoredToken() || hasCookieSession) && !!site,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

/**
 * Hydrates the unified tenant shell through an endpoint protected by the
 * principal's real legacy guard. HttpOnly SSO cookies work without a browser
 * token; local demo sessions pass their exact bearer explicitly.
 */
export const useUnifiedTenantGetUser = ({
  site,
  principalType,
  principalId,
  authGeneration,
  bearerToken,
  enabled,
}: {
  site?: string;
  principalType: UnifiedTenantPrincipalType;
  principalId?: string | number | null;
  authGeneration: number;
  bearerToken?: string | null;
  enabled: boolean;
}) =>
  useQuery({
    queryKey: unifiedTenantSessionQueryKey(
      site,
      principalType,
      principalId,
      authGeneration,
    ),
    queryFn: () => {
      if (!site) throw new Error('site is required to fetch unified tenant user');
      return getUnifiedTenantSession(site, principalType, bearerToken);
    },
    enabled: enabled && !!site,
    staleTime: 0,
    refetchOnMount: 'always',
    refetchOnWindowFocus: true,
    retry: false,
  });

export const useUnifiedTenantLogout = () =>
  useMutation({
    mutationFn: ({
      site,
      entryType,
      bearerToken,
    }: {
      site: string;
      entryType: SsoEntryType;
      bearerToken?: string | null;
    }) => unifiedLogout(site, entryType, bearerToken),
  });

export const useUnifiedTenantRefreshToken = () =>
  useMutation({
    mutationFn: ({
      site,
      entryType,
      bearerToken,
    }: {
      site: string;
      entryType: SsoEntryType;
      bearerToken?: string | null;
    }) => unifiedRefresh(site, entryType, bearerToken),
  });

/**
 * Hook to refresh authentication token
 */
export const useMainRefreshToken = () => {
  return useMutation({
    mutationFn: ({ site }: { site: string }) => refreshToken(site),
    onSuccess: (data) => {
      if (data.success && data.data?.token) {
        storeToken(data.data.token);
      }
    },
  });
};

/**
 * Hook for forgot password
 */
export const useMainForgotPassword = () => {
  return useMutation({
    mutationFn: ({ site, clinicNumber, loginId }: { site: string; clinicNumber: string; loginId: string }) =>
      forgotPassword(site, clinicNumber, loginId),
    // Avoid global "データが見つかりません。" toast for forgot-password.
    // This screen shows field-level validation UI instead.
    meta: { suppressNotFoundToast: true },
  });
};

/**
 * Hook to validate password reset token
 */
export const useMainValidateResetToken = () => {
  return useMutation({
    mutationFn: ({
      site,
      token,
      clinicNumber,
      loginId,
    }: {
      site: string;
      token: string;
      clinicNumber: string;
      loginId: string;
    }) => validateResetToken(site, token, clinicNumber, loginId),
  });
};

/**
 * Hook to reset password
 */
export const useMainResetPassword = () => {
  return useMutation({
    mutationFn: ({
      site,
      token,
      clinicNumber,
      loginId,
      password,
      passwordConfirmation,
    }: {
      site: string;
      token: string;
      clinicNumber: string;
      loginId: string;
      password: string;
      passwordConfirmation: string;
    }) => resetPassword(site, token, clinicNumber, loginId, password, passwordConfirmation),
    // Avoid global "データが見つかりません。" toast for reset-password.
    // This screen shows inline/form error UI instead.
    meta: { suppressNotFoundToast: true },
  });
};
